import { type ButtonHTMLAttributes, forwardRef } from "react"

import { type VariantProps, cva } from "class-variance-authority"

import { cn } from "@/lib/shadcn-utils"

const buttonVariants = cva(
  cn(
    "tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-text-nowrap",
    "tw-ring-offset-background tw-transition-colors",
    "focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-offset-1 focus-visible:tw-ring-ring",
    "disabled:tw-pointer-events-none disabled:tw-opacity-50",
    "[&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0",
  ),
  {
    variants: {
      variant: {
        default:
          "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90",
        destructive:
          "tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/90",
        outline:
          "tw-border tw-border-primary tw-text-primary tw-bg-background hover:tw-bg-accent",
        secondary:
          "tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80",
        ghost: "hover:tw-bg-accent hover:tw-text-accent-foreground",
        link: "tw-px-0 tw-text-primary tw-underline-offset-4 hover:tw-underline",
        outline_grey:
          "tw-bg-[#F1F1F2] tw-text-[#131822] tw-border tw-border-[#E8E8EA] hover:tw-bg-[#E8E8EA] focus-visible:tw-ring-0",
        selectable:
          "tw-border-2 tw-border-gray-200 tw-bg-white tw-text-gray-700 hover:tw-border-gray-300 hover:tw-bg-gray-50 data-[selected=true]:tw-border-green-500 data-[selected=true]:tw-bg-green-50 data-[selected=true]:tw-text-green-700",
      },
      size: {
        default: "tw-h-8 tw-px-4 tw-rounded-lg tw-text-base",
        sm: "tw-h-7 tw-px-3 tw-rounded-lg tw-text-sm",
        lg: "tw-h-11 tw-px-8 tw-rounded-md tw-text-lg",
        icon: "tw-h-10 tw-w-10 tw-rounded-md",
        card: "tw-px-12 tw-py-8 tw-rounded-xl tw-text-md tw-font-medium",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  selected?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, selected, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        data-selected={selected}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
